import { useCallback, useContext, useEffect, useRef } from 'react';
import {
  ActionIcon,
  AppShell,
  Container,
  Flex,
  Group,
  Loader,
  Stack,
  useMantineTheme,
} from '@mantine/core';
import { IconArrowLeft, IconX } from '@tabler/icons-react';
import Confetti from 'react-confetti';
import { useRouter } from 'next/router';
import Link from 'next/link';

import dt from '~/domains/contest/domains/manage/domains/common/testing.const';
import { ConfirmModal } from '~/domains/common/components/Modal/Confirm/ConfirmModal';
import UnknownErrorScreen from '~/domains/common/components/UnknownErrorScreen/UnknownErrorScreen';
import { Button, ButtonLabel } from '~/domains/design-system/Button';
import { useIsMobile } from '~/domains/common/hooks/mediaQueries';
import isWebView from '~/utils/isWebView';
import { Routes } from '~/domains/common/constants/routes';
import useRunOnPageLeave from '~/domains/common/hooks/useRunOnPageLeave';
import { isApiError } from '~/domains/services/utils/isApiError';
import { useNativeBrowserLoader } from '~/domains/common/utils/useNativeBrowserLoader';

import { SectionsProgressBar } from '../../common/sections/Sections/SectionsProgressBar';
import { SectionSubmitScreen } from '../../common/sections/Sections/SectionSubmitScreen';
import { useSectionStyles } from '../../common/sections/Sections/Sections.style';
import { SectionInProgressScreen } from '../../common/sections/Sections/SectionInProgressScreen';
import { SectionInProgressTemplate } from '../../common/sections/Sections/SectionInProgressTemplate';
import type { FormValues } from '../../common/types/FormValues';
import { useAnonymousContestCreation } from '../hooks/useAnonymousContestCreation';
import useSubmit from '../hooks/useSubmit';
import { useSectionsNavigation } from '../../common/context/hooks/useSectionsNavigation';
import { ContestManageContext } from '../../common/context/ContestManageContext';
import { SectionHeader } from '../../common/components/Header/Header';
import { reportCreateContestAbandon, reportCreateContestStart } from '../analytics';
import { SectionRedirectingScreen } from '../../common/sections/Sections/SectionRedirectingScreen';

import { SectionSwitch } from './SectionSwitch/SectionSwitch';

// 60 (header) + 16px (gap) + 74 (progress bar with header)
const MIN_HEIGHT = 150;

export function Sections() {
  const router = useRouter();
  const currentQuery = router.query;
  const inProgress = (currentQuery?.status as string) === 'inprogress';

  const theme = useMantineTheme();
  const webView = isWebView();

  const { classes, cx } = useSectionStyles();
  const isMobile = useIsMobile();
  const isNativeRedirecting = useNativeBrowserLoader();
  const isRedirecting = currentQuery?.redirecting;

  const {
    sections = [],
    isLoading,
    values,
    touched,
    allSectionsKeys,
    errors,
    error,
  } = useContext(ContestManageContext);
  const {
    currentSection,
    onNextSectionClick,
    isNextSection,
    onPrevSectionClick,
    isPrevSection,
    prevSectionKeyItem,
  } = useSectionsNavigation();
  const { hasValue, isAnonymous, isAnonURL } = useAnonymousContestCreation<FormValues>();
  const {
    isSubmitLoading,
    isSubmitVisible,
    onSubmit,
    isSubmitSuccess,
    onAnonymousSubmit,
    isDiscardChangesModalOpen,
    onDiscardChangesModalClose,
    onDiscardChangesModalConfirm,
    data,
  } = useSubmit();

  const hideBackButton =
    prevSectionKeyItem?.error === 'SECTION_NOT_LOADED' && prevSectionKeyItem?.key === 'initialStep';

  useEffect(() => {
    if (currentQuery?.redirecting && !isNativeRedirecting) {
      delete currentQuery.redirecting;
      router.push(
        {
          query: currentQuery,
        },
        undefined,
        { shallow: true }
      );
    }
  }, [currentQuery, isNativeRedirecting]);

  useEffect(() => {
    if (hasValue && isAnonURL) onSubmit();
  }, [hasValue, isAnonURL, onSubmit]);

  const getTitle = () => {
    if (inProgress) return 'Setting up your contest';
    if (isSubmitSuccess) return 'Contest created!';
    if (isPrevSection && isSubmitVisible && !isAnonymous) return 'Review contest settings';

    return 'Create a contest';
  };

  const isNextButtonDisabled = isLoading || !sections.length || !!errors?.[currentSection?.key];

  useEffect(() => {
    if (
      (currentSection?.key === allSectionsKeys[0]?.key && inProgress) ||
      (currentSection?.key === allSectionsKeys[allSectionsKeys.length - 1]?.key &&
        isPrevSection &&
        isSubmitVisible)
    ) {
      delete currentQuery.status;
      router.push({
        pathname: router.pathname,
        query: currentQuery,
      });
      return;
    }

    if (inProgress && !isNextButtonDisabled) {
      onNextSectionClick(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, isNextButtonDisabled, currentSection, isPrevSection, isSubmitVisible]);

  useEffect(() => {
    if (isSubmitSuccess) {
      return;
    }

    reportCreateContestStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSubmitSuccessRef = useRef(isSubmitSuccess);
  isSubmitSuccessRef.current = isSubmitSuccess;
  const abandonValuesRef = useRef<Parameters<typeof reportCreateContestAbandon>[0] | null>(null);

  abandonValuesRef.current = {
    values,
    errors,
    touched,
  };

  const handleAbandon = useCallback((e: BeforeUnloadEvent | PopStateEvent | Event) => {
    if (isSubmitSuccessRef.current) {
      return;
    }

    e.preventDefault();
    e.returnValue = '';

    reportCreateContestAbandon(abandonValuesRef.current);
  }, []);

  useRunOnPageLeave(handleAbandon);
  // Separate effect for component unmount
  useEffect(
    () => () => {
      if (isSubmitSuccessRef.current) {
        return;
      }

      reportCreateContestAbandon(abandonValuesRef.current);
    },
    []
  );

  return (
    <>
      {inProgress ? <SectionInProgressTemplate isNextSection={isNextSection} /> : null}
      <AppShell
        header={<SectionHeader contestName={values?.finalStep?.name} />}
        styles={{ main: { padding: 0 } }}
      >
        <div className={classes.pageWrapper} data-test-id={dt.container}>
          {isSubmitSuccess ? <Confetti recycle={false} /> : null}
          <Container size="md" pt={theme.other.spacing._40} className={classes.container}>
            <Stack className={classes.header} pos="relative" spacing={theme.other.spacing._16}>
              <Flex pos="relative" align="center">
                {!hideBackButton && isPrevSection && !isSubmitSuccess ? (
                  <ButtonLabel
                    variant="text"
                    className={cx(classes.backButton, { webView })}
                    data-test-id={dt.action.back}
                    onClick={onPrevSectionClick}
                  >
                    <IconArrowLeft />
                  </ButtonLabel>
                ) : null}
                <div className={cx(classes.title, { webView })}>{getTitle()}</div>
                {webView ? (
                  <Flex className={cx(classes.closeButton, { webView })} justify="end">
                    <ActionIcon
                      variant="transparent"
                      radius="xl"
                      component={Link}
                      href={Routes.home()}
                    >
                      <IconX color={theme.colors.gray2[9]} />
                    </ActionIcon>
                  </Flex>
                ) : null}
              </Flex>
              <SectionsProgressBar
                isFinal={hasValue}
                currentSectionKey={currentSection}
                allSectionsKeys={allSectionsKeys}
              />
            </Stack>
            <Flex
              justify="space-between"
              /* eslint-disable-line local-rules/use-theme-tokens */ gap={20}
              direction="column"
              mih={`calc(100vh - ${isMobile ? 16 : MIN_HEIGHT}px)`}
              pt="md"
              pos="relative"
            >
              {(() => {
                if (isSubmitLoading && hasValue) return <SectionInProgressScreen />;
                if (isSubmitSuccess) return <SectionSubmitScreen contestId={data.id} />;
                if (error && isApiError(error))
                  return <UnknownErrorScreen statusCode={error.response?.status} />;
                if (isRedirecting && isNativeRedirecting) return <SectionRedirectingScreen />;

                return (
                  <>
                    {sections.map((sectionKey) => (
                      <SectionSwitch key={sectionKey} sectionKey={sectionKey} />
                    ))}
                    <Group className={cx(classes.actions)}>
                      {isPrevSection && isNextSection && !isSubmitVisible && (
                        <Button
                          variant="primary-fill"
                          className={classes.button}
                          disabled={isNextButtonDisabled}
                          data-test-id={dt.action.next}
                          onClick={() => onNextSectionClick()}
                        >
                          Next
                        </Button>
                      )}
                      {isPrevSection && isSubmitVisible && !isAnonymous && (
                        <Button
                          variant="primary-fill"
                          className={classes.button}
                          disabled={isSubmitLoading}
                          onClick={onSubmit}
                          data-test-id={dt.action.submit}
                        >
                          {isSubmitLoading ? <Loader size={18} /> : 'Submit'}
                        </Button>
                      )}
                      {isSubmitVisible && isAnonymous && (
                        <Button
                          variant="primary-fill"
                          onClick={async () => {
                            await onAnonymousSubmit(values);
                          }}
                          disabled={isLoading || !sections.length}
                          data-test-id={dt.action.login}
                          className={classes.submit}
                        >
                          Log in to create
                        </Button>
                      )}
                    </Group>
                  </>
                );
              })()}
            </Flex>
          </Container>
          <ConfirmModal
            cancelText="Cancel"
            confirmText="Exit"
            description="All changes will be lost"
            isOpen={isDiscardChangesModalOpen}
            onClose={onDiscardChangesModalClose}
            onConfirm={onDiscardChangesModalConfirm}
            title="Are you sure you want to exit?"
          />
        </div>
      </AppShell>
    </>
  );
}
